/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import createLogger from 'vuex/dist/logger';
import { uniqueId, get } from 'lodash';

import { RootState } from '@/state/types';
import { Modal, ModalWithCallbacks, ModalSize } from './types';
import { FLAG_DYNAMIC_FORM, DYNAMIC_FORM_OBJECTS } from '@/constants/DynamicForm';

const debug = process.env.NODE_ENV !== 'production';

interface State {
  modals: ModalWithCallbacks[];
  size: ModalSize;
}

const state: State = {
  modals: [],
  size: ModalSize.Medium,
};

const handleNewObjectCreationModal = (modal: Modal, rootState: RootState): Modal => {
  const objectType = get(modal, 'data.type', '').toLowerCase();

  const shouldRenderDynamicForm =
    DYNAMIC_FORM_OBJECTS.includes(objectType) &&
    rootState.featureFlags[FLAG_DYNAMIC_FORM] &&
    modal.type === 'NewObject';

  if (shouldRenderDynamicForm) {
    return {
      ...modal,
      type: 'NewObjectPage',
      data: {
        ...modal.data,
        objectType,
      },
    };
  }
  return modal;
};

const mutations: MutationTree<State> = {
  ADD_MODAL(state: State, modal: ModalWithCallbacks) {
    state.modals.push(modal);
  },
  REMOVE_TOP_MODAL(state: State) {
    state.modals.pop();
  },
  SET_SIZE(state: State, newSize = ModalSize.Medium) {
    state.size = newSize;
  },
};

const actions: ActionTree<State, RootState> = {
  confirm({ getters, dispatch }: ActionContext, payload: any) {
    getters.active.resolve(payload);
    dispatch('hide');
  },
  cancel({ getters, dispatch }: ActionContext, payload: any) {
    getters.active.reject(payload);
    dispatch('hide');
  },
  hide({ commit }: ActionContext) {
    commit('REMOVE_TOP_MODAL');
    commit('SET_SIZE');
  },
  show({ commit, rootState }: ActionContext, modal: Modal) {
    const updatedModal = handleNewObjectCreationModal(modal, rootState);
    return new Promise((resolve, reject) => {
      const newModal = {
        data: {},
        ...updatedModal,
        id: uniqueId('modal_'),
        resolve,
        reject,
      };
      commit('ADD_MODAL', newModal);
      commit('SET_SIZE');
    });
  },
  changeSize({ commit }: ActionContext, newSize = ModalSize.Medium) {
    commit('SET_SIZE', newSize);
  },
};

const getters: GetterTree<State, RootState> = {
  active(state: State) {
    return state.modals[state.modals.length - 1];
  },
  size(state: State) {
    return state.size;
  },
  visible(state: State) {
    return state.modals.length > 0;
  },
};

export default (): Module<State, RootState> => ({
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  strict: debug,
  plugins: debug ? [createLogger()] : [],
});
